import { DonationMetadata, JourneyContext, Member } from "@flow/common/types";
import { MemberLoadingSpinner } from "@flow/frontend/member/components/MemberLoadingSpinner";

import useScript from "@flow/lib/use_script";
import { Typography } from "@mui/material";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { createPaypalDonationAttempt, createPaypalSubscriptionPlan } from "../../common/routes";

// from paypal JS loaded with useScript
declare const paypal: any;

// from registerTemplateVariable
declare const PAYPAL_CLIENT_ID: string;
const paypal_url = "https://www.paypal.com/sdk/js";

export function PaypalCheckout(props: {
  ctx: JourneyContext;
  action_guid: string;
  variant_idx: number;
  amount: number;
  metadata: DonationMetadata;
  hide_donation: boolean;
  isRegular: boolean;
  onPaymentSuccess: (donation_attempt_guid: string, member: Member) => void;
}) {
  const [ready, setReady] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    useScript(
      `${paypal_url}?client-id=${PAYPAL_CLIENT_ID}&enable-funding=venmo&currency=GBP&disable-funding=credit,card&vault=true`
    ).then(() => setReady(true));
  }, []);

  const hiddenSpanElem = React.useRef(null);

  React.useEffect(() => {
    if (hiddenSpanElem.current) {
      (hiddenSpanElem.current as any).dispatchEvent(
        new CustomEvent("amount-event", { detail: { amount: props.amount } })
      );
    }
    if (props.amount >= 0) {
      setErrorMessage(undefined);
    }
  }, [props.amount]);

  function createOrder(data: any, actions: any) {
    return actions.order.create({
      purchase_units: [
        {
          // Nasty hack. Speakout forwards all paypal IPN events to ID, which causes
          // duplicate donation entries (since flow pushes donations to ID itself).
          // Here we pass a custom value (which appears in the IPN payload as 'custom' key),
          // which speakout can detect and drop
          custom_id: "origin_flow__speakout_please_do_not_forward_to_id",

          amount: {
            currency_code: "GBP",
            value: props.amount.toString(),
          },
        },
      ],
      application_context: {
        //shipping_preference: shippingPreference
      },
    });
  }

  async function createSubscription(data: any, actions: any): Promise<any> {
    const { plan_id } = await createPaypalSubscriptionPlan.call({ amount: props.amount });

    return actions.subscription.create({
      plan_id: plan_id,
    });
  }

  function onApprove(data: any, actions: any) {
    // on successful regular payment, got this:
    // data = { orderID: "8XW08204Y4933502Y", paymentID: null, billingToken: "BA-9UC24695T54015748", subscriptionID: "I-U8U15KVXGVHE", facilitatorAccessToken: "A21AAK6FnL1lpqdeS6pot-WheC-7Na7dRN1lVCnlQwXKXY4sm3koFBy9JduVJOFKBKIjGgTiqqYQExkvwejxNXAIjFDcbOG-g" }
    // on one-off payment:
    // data = { "orderID": "1E7607464L810184V", "payerID": "8D3VVPC7E2T76", "paymentID": null, "billingToken": null, "facilitatorAccessToken": "A21AALeufv4qQAkBBGDro62B40TMHafIEshUwlZyGafI9AgRvYAhsDzbfo-SCBKENAt6GQKKYFMgAjpJ8f9dknVzLC7Y9JJbQ" }

    createPaypalDonationAttempt
      .call({
        journey_context: props.ctx,
        action_guid: props.action_guid,
        variant_idx: props.variant_idx,
        order_id: data.orderID,
        subscription_id: data.subscriptionID,
        amount: props.amount,
        metadata: { boost_amount: props.metadata.boost_amount },
        hide_donation: props.hide_donation,
        frequency: props.isRegular ? "monthly" : "once",
        currency: "GBP",
      })
      .then(({ donation_attempt_guid }) => {
        props.onPaymentSuccess(donation_attempt_guid, props.ctx.member);
      });
  }

  function handlePayPalButtonClick() {
    // The PayPal magic Button component we use does not appear to expose a prop to control disabled state
    // so instead we just prevent the default action if the amount is not set and render an error message
    if (props.amount <= 0) {
      setErrorMessage("Please input an amount before selecting payment method");
    }
  }

  function handlePayPalButtonInit(
    data: Record<string, unknown>,
    actions: {
      enable: () => Promise<void>;
      disable: () => Promise<void>;
    }
  ) {
    if (props.amount > 0) {
      actions.enable();
    } else {
      actions.disable();
    }

    document.getElementById("amount-event-hook")?.addEventListener("amount-event", ((e: CustomEvent) => {
      if (e.detail.amount && e.detail.amount > 0) {
        actions.enable();
      }
    }) as EventListener);
  }

  // @ts-ignore
  if (ready && window.paypal !== undefined) {
    // @ts-ignore
    const Button = window.paypal.Buttons.driver("react", { React, ReactDOM });

    return (
      <div style={{ height: errorMessage ? 57 : 37 }}>
        <span id="amount-event-hook" ref={hiddenSpanElem} hidden />
        <div style={{ textAlign: "center" }}>
          <span style={{ display: props.isRegular ? "inline" : "none" }}>
            <Button
              style={{ height: 37 }}
              disabled={props.isRegular && props.amount <= 0}
              key={"paypal-subscription"}
              createSubscription={createSubscription}
              amount={props.amount.toString()}
              onApprove={onApprove}
              onInit={handlePayPalButtonInit}
              onClick={handlePayPalButtonClick}
            />
          </span>
          <span style={{ display: props.isRegular ? "none" : "inline" }}>
            <Button
              style={{ height: 37 }}
              disabled={!props.isRegular && props.amount <= 0}
              key={"paypal-oneoff"}
              createOrder={createOrder}
              amount={props.amount.toString()}
              onApprove={onApprove}
              onInit={handlePayPalButtonInit}
              onClick={handlePayPalButtonClick}
            />
          </span>
          {errorMessage && (
            <Typography variant="body2" color="error" fontWeight="bold">
              {errorMessage}
            </Typography>
          )}
        </div>
      </div>
    );
  } else {
    return <MemberLoadingSpinner />;
  }
}
